import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../utils/api';
import { useToast } from './common/ToastContext';

export default function GitHubPRCommentModal({ open, onClose, repoName, sbomId }) {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const [owner, setOwner] = useState('');
  const [repo, setRepo] = useState('');
  const [prNumber, setPrNumber] = useState('');
  const [token, setToken] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');
  const [commentUrl, setCommentUrl] = useState('');

  useEffect(() => {
    if (open) {
      setError('');
      setCommentUrl('');
      authFetch('/settings/smtp').catch(() => {});
      authFetch('/integrations/github/settings')
        .then((r) => r.ok ? r.json() : null)
        .then((data) => {
          if (data) {
            if (data.githubDefaultOwner) setOwner(data.githubDefaultOwner);
            if (data.githubDefaultRepo) setRepo(data.githubDefaultRepo);
          }
        })
        .catch(() => {});
    }
  }, [open]);

  async function handlePost() {
    if (!owner.trim() || !repo.trim() || !prNumber) {
      setError(t('githubPR.errorRequired', 'Owner, repository, and PR number are required.'));
      return;
    }
    setPosting(true);
    setError('');
    setCommentUrl('');
    try {
      const res = await authFetch(
        `/sbom/${encodeURIComponent(repoName)}/${encodeURIComponent(sbomId)}/github-pr-comment`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            owner: owner.trim(),
            repo: repo.trim(),
            prNumber: parseInt(prNumber, 10),
            githubToken: token.trim(),
          }),
        },
      );
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t('githubPR.errorGeneric', 'Failed to post comment.'));
      } else {
        setCommentUrl(data.commentUrl || '');
        showToast(t('githubPR.successToast', 'Comment posted successfully!'), 'success');
      }
    } catch (e) {
      setError(t('githubPR.errorGeneric', 'Failed to post comment.'));
    } finally {
      setPosting(false);
    }
  }

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-card, #1e1e2e)', borderRadius: 10, padding: '1.75rem',
          width: '100%', maxWidth: 480, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: '0 0 1.25rem', fontSize: '1.1rem' }}>
          🐙 {t('githubPR.title', 'Post PR Comment to GitHub')}
        </h3>

        <div style={{ marginBottom: '0.875rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
            {t('githubPR.ownerLabel', 'Owner')}
          </label>
          <input
            type="text"
            placeholder="octocat"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input, #2a2a3e)', color: 'inherit' }}
          />
        </div>

        <div style={{ marginBottom: '0.875rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
            {t('githubPR.repoLabel', 'Repository')}
          </label>
          <input
            type="text"
            placeholder="my-repo"
            value={repo}
            onChange={(e) => setRepo(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input, #2a2a3e)', color: 'inherit' }}
          />
        </div>

        <div style={{ marginBottom: '0.875rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
            {t('githubPR.prNumberLabel', 'PR Number')}
          </label>
          <input
            type="number"
            placeholder="42"
            value={prNumber}
            onChange={(e) => setPrNumber(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input, #2a2a3e)', color: 'inherit' }}
          />
        </div>

        <div style={{ marginBottom: '1.25rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
            {t('githubPR.tokenLabel', 'GitHub Token')}
          </label>
          <input
            type="password"
            placeholder="ghp_... (uses default if empty)"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input, #2a2a3e)', color: 'inherit' }}
          />
          <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: 4, display: 'block' }}>
            {t('githubPR.tokenHint', 'Leave blank to use the default token configured in GitHub settings.')}
          </small>
        </div>

        {error && (
          <div style={{ color: '#ef4444', fontSize: '0.875rem', marginBottom: '0.875rem', padding: '0.5rem 0.75rem', background: 'rgba(239,68,68,0.1)', borderRadius: 6 }}>
            {error}
          </div>
        )}

        {commentUrl && (
          <div style={{ marginBottom: '0.875rem', padding: '0.5rem 0.75rem', background: 'rgba(34,197,94,0.1)', borderRadius: 6, fontSize: '0.875rem' }}>
            ✅ {t('githubPR.successMsg', 'Comment posted!')}{' '}
            <a href={commentUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#22c55e' }}>
              {t('githubPR.viewComment', 'View on GitHub')}
            </a>
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button className="ce-btn ce-btn--secondary ce-btn--sm" onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            className="ce-btn ce-btn--primary ce-btn--sm"
            onClick={handlePost}
            disabled={posting}
          >
            {posting ? t('githubPR.posting', 'Posting…') : t('githubPR.postBtn', 'Post Comment')}
          </button>
        </div>
      </div>
    </div>
  );
}
